import React, { useState, useEffect, useCallback } from 'react';
import { 
  ITextareaComponent,
  IFormControlChange,
  IFormValidationErrors
} from '../df-form-preview-interfaces';
import { DfFormErrorMsg } from '../df-form-error-msg';

interface DfFormTextareaProps {
  id: string;
  properties: ITextareaComponent;
  validationErrors?: IFormValidationErrors;
  formValue?: string;
  readonly?: boolean;
  disabled?: boolean;
  touchedFields?: Record<string, boolean>;
  formSubmitted?: boolean;
  mode?: 'edit' | 'preview' | 'test';
  onValueChange?: (change: IFormControlChange) => void;
  onBlur?: () => void;
  onFocus?: () => void;
  className?: string;
  hideLabel?: boolean;
}

export const DfFormTextarea: React.FC<DfFormTextareaProps> = ({
  id,
  properties,
  validationErrors = {},
  formValue = '',
  readonly = false,
  disabled = false,
  touchedFields = {},
  formSubmitted = false,
  mode = 'preview',
  onValueChange,
  onBlur,
  onFocus,
  className = '',
  hideLabel = false
}) => {
  const [value, setValue] = useState<string>(formValue);
  const [isTouched, setIsTouched] = useState<boolean>(false);

  // Validate field
  const validateField = useCallback((val: string): { isValid: boolean; errors: Record<string, any> } => {
    const errors: Record<string, any> = {};
    let isValid = true;

    // Required validation
    if (properties?.validation?.required && (!val || val.trim() === '')) {
      errors.required = true;
      isValid = false;
    }

    // Length validations
    if (properties?.validation?.minLength && val && val.length < properties.validation.minLength) {
      errors.minLength = true;
      isValid = false;
    }

    if (properties?.validation?.maxLength && val && val.length > properties.validation.maxLength) {
      errors.maxLength = true;
      isValid = false;
    }

    return { isValid, errors };
  }, [properties]);

  // Handle value changes
  const handleValueChange = useCallback((newValue: string) => {
    setValue(newValue);
    
    if (onValueChange) {
      const validation = validateField(newValue);
      const change: IFormControlChange = {
        id,
        value: newValue,
        isValid: validation.isValid,
        errors: validation.errors,
      };
      onValueChange(change);
    }
  }, [id, onValueChange, validateField]);

  // Handle textarea change
  const handleTextareaChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    const newValue = e.target.value;
    handleValueChange(newValue);
  };

  // Handle focus
  const handleFocus = () => {
    if (onFocus) {
      onFocus();
    }
  };

  // Handle blur
  const handleBlur = () => {
    setIsTouched(true);
    if (onBlur) {
      onBlur();
    }
  };

  // Update touched fields
  useEffect(() => {
    if (isTouched) {
      touchedFields[id] = true;
    }
  }, [isTouched, id, touchedFields]);

  // Update value when formValue prop changes (only on initial load or when component is reset)
  useEffect(() => {
    // Only update if the current value is empty (initial state) or if formValue is explicitly set
    // This prevents the component from resetting user input when they blur the field
    if (value === '' && formValue !== '') {
      setValue(formValue);
    }
  }, [formValue]); // Remove value and isFocused from dependencies to prevent reset on blur

  // Mark as touched when form is submitted
  useEffect(() => {
    if (formSubmitted) {
      setIsTouched(true);
    }
  }, [formSubmitted]);

  // Clear value when switching away from test mode
  useEffect(() => {
    if (mode !== 'test') {
      setValue('');
    }
  }, [mode]);

  // Get label alignment class
  const getLabelAlignmentClass = (): string => {
    const alignment = properties?.styles?.labelAlignment;
    return alignment === 'left' ? 'label-left' : 'label-top';
  };

  // Get textarea classes
  const getTextareaClasses = (): string => {
    const base = 'form-textarea';
    const validation = validateField(value);
    const error = !validation.isValid && isTouched ? 'is-invalid' : '';
    return `${base} ${error} ${className}`.trim();
  };


  const shouldDisable = disabled || readonly || properties?.validation?.readonly;
  const rows = properties?.validation?.rows || 4;

  return (
    <div className={`form-group ${getLabelAlignmentClass()}`}>
      {!hideLabel && properties.basic.label && (
        <label htmlFor={id} className="form-label">
          {properties.basic.label}
          {properties.validation.required && (
            <span className="required-indicator">*</span>
          )}
        </label>
      )}

      <div className="input-wrapper">
        <textarea
          id={id}
          value={value}
          onChange={handleTextareaChange}
          onFocus={handleFocus}
          onBlur={handleBlur}
          placeholder={properties.basic.placeholder}
          disabled={shouldDisable}
          readOnly={readonly || properties?.validation?.readonly}
          rows={rows}
          className={getTextareaClasses()}
        />

        <DfFormErrorMsg
          validationErrors={validationErrors}
          fieldId={id}
          touchedFields={touchedFields}
          formSubmitted={formSubmitted}
          properties={properties}
          localValidation={validateField(value)}
          isTouched={isTouched}
          mode={mode}
        />
      </div>
    </div>
  );
};

export default DfFormTextarea;
